import _, {chain, filter, sortBy} from 'lodash';
import {createSelector} from 'reselect';

import {isTeammateActive} from '../../../helpers/modelHelpers';
import {isFrontChatConversation} from '../../../helpers/models/conversationHelpers';
import {isMessageVisuallyShared} from '../../../helpers/models/messageHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {CommentModel, CommentStatusesEnum} from '../../../models/api/commentModel';
import {DraftSeedModel} from '../../../models/api/draftSeedModel';
import {DraftMessageModel, MessageStatusesEnum} from '../../../models/api/messageModel';
import {PrecogTypesEnum} from '../../../models/api/precogModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {PrecogsSelector} from '../../data/conversations/conversationsPrecogsSelectors';
import {
  ConversationSelector,
  LoadedDraftMessagesSelector,
  makeGetDraftComments,
} from '../../data/conversations/conversationsSelectors';
import {getActivities, getConversations} from '../../data/dataSelectors';
import {makeGetIsPanelDraftOwner} from '../../data/messages/messagesSelectors';
import {getFindTeammate} from '../../data/teammates/lazyTeammatesSelectors';
import {getTeammates, getUserId} from '../../data/teammates/teammatesSelectors';
import {getHasAutoShareDrafts, getHasCommentTombstone} from '../../featureFlags/featureFlagsSelectors';
import {getFlatPanels, getPanels} from '../panelsSelectors';
import {PanelState} from '../panelsState';
import {PanelTypesEnum, ViewerPanelId} from '../panelStateBase';
import {
  ViewerPanelConversationPageState,
  ViewerPanelPageState,
  ViewerPanelPageTypesEnum,
  ViewerPanelState,
} from './viewerPanelState';

/*
 * Types.
 */

type CommentsSelector<P> = AppParametricSelector<P, ReadonlyArray<CommentModel>>;

export interface SendingActivity {
  comment?: CommentModel;
  message?: DraftMessageModel;
  uid: string;
  sentAt: number;
}

/*
 * Viewer panel.
 */

export interface ViewerPanelProps {
  panelId: ViewerPanelId;
}

export type ViewerPanelIdSelector<P> = AppParametricSelector<P, ViewerPanelId>;

function getViewerPanelId(_state: unknown, props: ViewerPanelProps) {
  return props.panelId;
}

export type ViewerPanelSelector<P> = AppParametricSelector<P, ViewerPanelState>;

export function makeGetViewerPanel(): ViewerPanelSelector<ViewerPanelProps>;
export function makeGetViewerPanel<P>(getPanelId: ViewerPanelIdSelector<P>): ViewerPanelSelector<P>;
export function makeGetViewerPanel(getPanelId: ViewerPanelIdSelector<any> = getViewerPanelId) {
  return createSelector([getPanels, getPanelId], (panels, panelId) => {
    const panel = panels[panelId];

    // A corresponding panel should exist, otherwise we have a problem.
    if (!panel || panel.type !== PanelTypesEnum.VIEWER) {
      throw new Error('This viewer panel could not be found.');
    }

    return panel;
  });
}

/*
 * Viewer panel pages.
 */

export type ViewerPanelPageSelector<P> = AppParametricSelector<P, ViewerPanelPageState>;

export function makeGetViewerPanelPage(): ViewerPanelPageSelector<ViewerPanelProps>;
export function makeGetViewerPanelPage<P>(getPanelId: ViewerPanelIdSelector<P>): ViewerPanelPageSelector<P>;
export function makeGetViewerPanelPage(getPanelId: ViewerPanelIdSelector<any> = getViewerPanelId) {
  return createSelector(makeGetViewerPanel(getPanelId), ({pages}) => pages.next ?? pages.current);
}

/** Find the current topbar page for the specified viewer panel. */
export function makeGetTopbarPage(): ViewerPanelPageSelector<ViewerPanelProps>;
export function makeGetTopbarPage<P>(getViewerPanel: ViewerPanelSelector<P>): ViewerPanelPageSelector<P>;
export function makeGetTopbarPage(getViewerPanel: ViewerPanelSelector<any> = makeGetViewerPanel()) {
  return createSelector([getViewerPanel], ({pages}) => pages.current);
}

/** Find the current content page for the specified viewer panel. */
export function makeGetContentPage(): ViewerPanelPageSelector<ViewerPanelProps>;
export function makeGetContentPage<P>(getViewerPanel: ViewerPanelSelector<P>): ViewerPanelPageSelector<P>;
export function makeGetContentPage(getViewerPanel: ViewerPanelSelector<any> = makeGetViewerPanel()) {
  return createSelector(getViewerPanel, ({pages}) => pages.current);
}

export type ViewerPanelConversationPageSelector<P> = AppParametricSelector<
  P,
  ViewerPanelConversationPageState
>;

/** Find the activity IDs for the specified viewer page. */
function makeGetPageActivityIds<P>(getPage: ViewerPanelConversationPageSelector<P>) {
  return createSelector(getPage, (page) =>
    _(page.activities)
      .map((a) => a && a.activityId)
      .compact()
      .value(),
  );
}

/** Find the activities for the specified viewer page. */
export type ActivitiesSelector<P> = AppParametricSelector<P, ReadonlyArray<ActivityModel>>;
export function makeGetPageActivities<P>(
  getPage: ViewerPanelConversationPageSelector<P>,
): ActivitiesSelector<P> {
  const getActivityIds = wrapInShallowSelector(makeGetPageActivityIds(getPage));
  return createSelector(
    [getActivityIds, getActivities, getHasCommentTombstone],
    (activityIds, activities, hasCommentTombstone) => {
      if (hasCommentTombstone) {
        return _(activityIds)
          .map((id) => activities[id])
          .compact()
          .filter((a) => a.deletedAt === undefined)
          .orderBy([(a) => a.date, (a) => a.id])
          .value();
      }
      // If the comment-tombstone FF is rolled back after being turned on, we may still have tombstoned
      // activities in our state. It's necessary to reject any tombstoned activities in this case.
      return _(activityIds)
        .map((id) => activities[id])
        .compact()
        .filter((a) => a.deletedAt === undefined)
        .reject((a) => a.visualType === ActivityVisualTypesEnum.COMMENT && a.isTombstoned === true)
        .orderBy([(a) => a.date, (a) => a.id])
        .value();
    },
  );
}

/*
 * Global selectors.
 */

/** Map a viewer panel to its two constituting pages. */
function findViewerPanelConversationPages(panelState: PanelState) {
  // If this is not a viewer panel, nothing to extract.
  if (panelState.type !== PanelTypesEnum.VIEWER) {
    return null;
  }

  // Otherwise, return both the current and next pages.
  return _([panelState.pages.current, panelState.pages.next])
    .compact()
    .map((page) => page.type === ViewerPanelPageTypesEnum.SINGLE_CONVERSATION && page)
    .compact()
    .value();
}

/** Find the IDs of conversations currently being displayed in a viewer. */
export const getViewerPanelsConversationIds = createSelector(getFlatPanels, (panels) =>
  _(panels)
    .map(findViewerPanelConversationPages) // Extract the pages.
    .compact() // Remove null page arrays.
    .flatten() // Make it an array of pages.
    .compact() // Remove undefined pages.
    .map((page) => page.conversationId) // Extract the conversation ID.
    .uniq() // Deduplicate.
    .value(),
);

/** Find the IDs of chat conversations currently being displayed in a viewer. */
export const getViewerPanelsChatConversationIds = createSelector(
  [getConversations, getViewerPanelsConversationIds],
  (conversations, conversationIds) =>
    conversationIds.filter((id) => {
      const conversation = conversations[id];
      return conversation && isFrontChatConversation(conversation);
    }),
);

export const getViewerPanelsRooms = createSelector(getViewerPanelsConversationIds, (conversationIds) =>
  conversationIds.map((id) => `conversation-${id}`),
);

export function makeGetViewerPanelsForConversations<P>(
  getSourceConversationIds: AppParametricSelector<P, ReadonlyArray<number>>,
) {
  return createSelector([getFlatPanels, getSourceConversationIds], (panels, sourceConversationIds) =>
    _(panels)
      .map((p) => p.type === PanelTypesEnum.VIEWER && p)
      .compact()
      .map((panel) =>
        _(findViewerPanelConversationPages(panel))
          .compact()
          .map(({conversationId, parameters, cellsContext}) => {
            if (!sourceConversationIds.includes(conversationId)) {
              return undefined;
            }

            return {
              panelId: panel.id,
              parameters,
              cellsContext,
            };
          })
          .value(),
      )
      .flatten()
      .compact()
      .value(),
  );
}

export const getFirstDiscussionComposerPanel = createSelector([getFlatPanels], (panels) =>
  _(panels)
    .map((panel) => panel.type === PanelTypesEnum.VIEWER && panel.composeCommentUid && panel)
    .compact()
    .first(),
);

export interface DraftSeedWithAuthor extends DraftSeedModel {
  author: TeammateModel;
}

type DraftSeedWithAuthorSelector<P> = AppParametricSelector<P, ReadonlyArray<DraftSeedWithAuthor>>;
export function makeGetDraftSeedsWithAuthor<P>(
  getConversation: ConversationSelector<P>,
): DraftSeedWithAuthorSelector<P> {
  return createSelector([getConversation, getTeammates], (conversation, teammates) =>
    _(conversation.draftSeeds)
      .uniqBy((s) => s.authorId)
      .map((seed) => {
        const author = teammates[seed.authorId];
        if (!author) {
          return undefined;
        }

        if (!isTeammateActive(author)) {
          return undefined;
        }

        return {
          ...seed,
          author,
        };
      })
      .compact()
      .sortBy(({author}) => (author.givenName + author.familyName).toLowerCase())
      .value(),
  );
}

export function makeGetDraftSeedAuthors<P>(getConversation: ConversationSelector<P>) {
  return createSelector([getConversation, getFindTeammate], (conversation, findTeammate) =>
    _(conversation.draftSeeds)
      .uniqBy((s) => s.authorId)
      .map((s) => findTeammate(s.authorId, 'viewerPanelSelectors makeGetDraftSeedAuthors'))
      .compact()
      .filter((t) => isTeammateActive(t))
      .sortBy((t) => (t.givenName + t.familyName).toLowerCase())
      .value(),
  );
}

export function makeGetSortedComposeDraftMessages<P>(getDraftMessages: LoadedDraftMessagesSelector<P>) {
  return createSelector(
    [getDraftMessages, getUserId, getConversations, getHasAutoShareDrafts],
    (draftMessages, userId, conversations, hasAutoShareDrafts) =>
      _(draftMessages)
        .filter((d) => d.status === MessageStatusesEnum.DRAFT_COMPOSE)
        .sortBy(
          (d) =>
            !isMessageVisuallyShared(d, conversations[d.conversationId], hasAutoShareDrafts) ||
            d.authorId === userId,
          (d) => d.createdAt,
        )
        .value(),
  );
}

export function makeGetReadonlyDraftMessageUids<P>(
  getDraftMessages: LoadedDraftMessagesSelector<P>,
  getPanelId: ViewerPanelIdSelector<P>,
) {
  return createSelector(
    [getDraftMessages, makeGetIsPanelDraftOwner(getPanelId)],
    (draftMessages, isPanelDraftOwner) =>
      _(draftMessages)
        .filter((draft) => !isPanelDraftOwner(draft.uid))
        .map((draft) => draft.uid)
        .value(),
  );
}

export function makeGetReplyingTeammates<P>(getPrecogs: PrecogsSelector<P>) {
  return createSelector([getPrecogs, getFindTeammate], (precogs, findTeammate) =>
    chain(precogs)
      .filter((p) => p.type === PrecogTypesEnum.MESSAGE)
      .map((p) => findTeammate(p.teammateId, 'viewerPanelSelectors makeGetReplyingTeammates'))
      .compact()
      .uniqBy((t) => t.id)
      .value(),
  );
}

export function makeGetCommentingTeammates<P>(getPrecogs: PrecogsSelector<P>) {
  return createSelector([getPrecogs, getFindTeammate], (precogs, findTeammate) =>
    chain(precogs)
      .filter((p) => p.type === PrecogTypesEnum.COMMENT)
      .map((p) => findTeammate(p.teammateId, 'viewerPanelSelectors makeGetCommentingTeammates'))
      .compact()
      .uniqBy((t) => t.id)
      .value(),
  );
}

const sendingCommentStatuses = [
  CommentStatusesEnum.PENDING,
  CommentStatusesEnum.SENDING,
  CommentStatusesEnum.FAILED,
];

export function makeGetSendingComments<P>(getConversation: ConversationSelector<P>): CommentsSelector<P> {
  return createSelector(makeGetDraftComments(getConversation), (draftComments) =>
    filter(draftComments, (c) => sendingCommentStatuses.includes(c.status)),
  );
}

/*
 * Page contexts.
 */

// List of message statuses to display
const sendingMessageStatuses = [
  MessageStatusesEnum.DRAFT_PENDING,
  MessageStatusesEnum.DRAFT_SENDING,
  MessageStatusesEnum.DRAFT_FAILED,
  MessageStatusesEnum.DRAFT_LATER,
];

export type SendingActivitiesSelector<P> = AppParametricSelector<P, ReadonlyArray<SendingActivity>>;
export function makeGetSendingActivities<P>(
  getSendingComments: CommentsSelector<P>,
  getDraftMessages: LoadedDraftMessagesSelector<P>,
): SendingActivitiesSelector<P> {
  return createSelector([getSendingComments, getDraftMessages], (sendingComments, draftMessages) => {
    const sendingCommentActivities: ReadonlyArray<SendingActivity> = sendingComments.map((c) => ({
      comment: c,
      uid: c.uid,
      sentAt: c.sentAt,
    }));

    const sendingMessageActivities: ReadonlyArray<SendingActivity> = chain(draftMessages)
      .filter((m) => sendingMessageStatuses.includes(m.status))
      .map((m) => ({
        message: m,
        uid: m.uid,
        sentAt: m.sentAt,
      }))
      .value();

    const combined = sendingCommentActivities.concat(sendingMessageActivities);
    return sortBy(combined, (d) => d.sentAt);
  });
}
